import type { CardSignal } from "@/surfaces/read/cardSignal";

/**
 * What a key means over a list of dictionary results — pure, so it is testable (日本-497).
 *
 * `sessionRules.ts` is the precedent and the reason: the interesting bugs in a keyboard surface are
 * in *which key does what when*, and that question has no DOM in it. `web/` has no rendering tests,
 * so a rule left inline in a `keydown` handler is checked by reading and by nothing else.
 *
 * **Both surfaces get the same four keys** — `a` adds, `e` edits, `j`/`k` and the arrows move. 検索
 * has a text field where Read does not, and the thing that makes the bare letters safe there is the
 * IME rather than a rule of its own: with a Japanese IME on, a keystroke arrives as `Process` /
 * keyCode 229 or with `isComposing` set, and never as the letter. See `isImeKeystroke`.
 *
 * **Enter is deliberately NOT an add key**, and this reverses what shipped first. It looked like
 * 検索's fast path — type the word, press Enter, done — and it cannot be, because **Enter is the key
 * the IME uses to commit a conversion**. Reproduced: accept 猫, press Enter again straight away and
 * nothing happens (the commit is still settling and the query has not been re-run); press it once
 * more and a card appears. Nothing can tell "commit" from "add" on one key, so the fast path is
 * `A` — which is also what Read has trained, and what Alex asked for.
 */

export type DictionaryAction = "add" | "edit" | "open" | "next" | "prev";

export interface KeyContext {
  /** Whether a result is focused at all. No results means no key does anything. */
  hasFocus: boolean;
  /** What the collection says about the focused row's term. */
  signal: CardSignal;
  /** Whether the focused row's card already holds THIS entry. */
  onCard: boolean;
  /**
   * Whether the focused row is a 文法 entry (日本-519).
   *
   * `O` opens it in 文法 and `A`/`E` do nothing on it — a grammar entry is not something the
   * collection can hold, so there is no card to add and none to edit. Passed in rather than
   * derived from `signal`, because the two questions are unrelated now: `signal` is about a *word*,
   * and this row is not one.
   */
  isGrammar?: boolean;
  /**
   * Whether a composition is in flight, as the SURFACE understands it.
   *
   * OR-ed with the event's own IME markers below rather than relied on alone: a component's
   * `composing` flag is set by `compositionstart`, which arrives *after* the first keydown of the
   * composition — so on its own it lets exactly one raw letter through, which on this screen is one
   * card added while typing.
   */
  composing: boolean;
}

/**
 * Whether this keystroke belongs to an input method rather than to the reader.
 *
 * **This is what makes a bare `a` safe inside a search box.** While a Japanese IME is converting,
 * Chrome reports `key: "Process"` (and `keyCode: 229` on Windows) and sets `isComposing` once the
 * composition has started — so a keydown that really says `"a"` means the IME is off. Three tests
 * rather than one because they cover different moments: `isComposing` misses the first keystroke,
 * `Process` is what that first keystroke actually reports, and 229 is the older WebView spelling
 * that Ask's composer already guards against for the same reason.
 *
 * The residue, stated rather than hidden: with the IME switched **off**, pressing `a` over a focused
 * result adds a card instead of typing an `a`. That needs a successful Japanese lookup already on
 * screen to do anything at all (`hasFocus`), and a Latin letter cannot contribute to one — this
 * dictionary is Japanese-only — so the reachable case is narrow and the trade is Read's.
 */
function isImeKeystroke(
  event: Pick<KeyboardEvent, "key"> & { isComposing?: boolean; keyCode?: number },
): boolean {
  return event.isComposing === true || event.key === "Process" || event.keyCode === 229;
}

export function dictionaryAction(
  event: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey"> & {
    isComposing?: boolean;
    keyCode?: number;
  },
  context: KeyContext,
): DictionaryAction | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (context.composing || isImeKeystroke(event)) return null;
  if (!context.hasFocus) return null;

  if (event.key === "ArrowDown" || event.key === "j") return "next";
  if (event.key === "ArrowUp" || event.key === "k") return "prev";

  // **A 文法 row answers `O` and nothing else** (日本-519). It used to answer `A` by making a card
  // and `E` by editing one; grammar stopped being a card type, so both would now act on a
  // collection that has nothing to act on. Returning null for them rather than falling through is
  // what stops `A` reaching `lookup.add`, which wants a `Definition` this row does not have.
  if (context.isGrammar) {
    return event.key === "o" || event.key === "O" ? "open" : null;
  }
  // The same condition `ActionButton` uses to draw an add button, because that button advertises
  // this key. A carded word whose card does not yet hold THIS definition offers `+ カードに追加`,
  // and gating on `known` alone made the hint a lie for every one of them.
  if (event.key === "a" || event.key === "A") return canAdd(context) ? "add" : null;
  // `=== "known"`, never `!== "unknown"`. `E` reaches `by-term`, which does not resolve a term to
  // another card's headword — so offering it on an `elsewhere` row would 404 into an empty catch and
  // make the key do nothing at all.
  if (event.key === "e" || event.key === "E") {
    return context.signal.state === "known" ? "edit" : null;
  }
  return null;
}

function canAdd(context: KeyContext): boolean {
  return context.signal.state !== "known" || !context.onCard;
}
